import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createFollowUp,
  getFollowUps,
  patchFollowUp,
  type RequestFollowUp,
} from '../../../shared/api/followUps.api'
import { getQuotations } from '../../../shared/api/quotations.api'
import { getRequestActivity } from '../../../shared/api/requests.api'
import type { RequestStatus } from '../../../shared/api/types'
import { confirm } from '../../../shared/ui/confirmStore'
import { toast } from '../../../shared/ui/toastStore'
import { FollowUpModal } from './FollowUpModal'
import {
  IconArchive,
  IconBell,
  IconCheckCircle,
  IconChevron,
  IconDocument,
  IconSend,
  IconXCircle,
} from './kpControlIcons'

interface Props {
  requestId: string
  /** request.status — для строки «заявка закрыта» (Счёт/Отказ/Архив). */
  status: RequestStatus
  /** request.quoteSentAt — дата «КП отправлено» (последняя). */
  quoteSentAt: string | null
  /** Право ставить/закрывать напоминания (менеджер/РОП/помощник/админ). */
  canEdit: boolean
}

/** Терминальные статусы заявки — в блоке показывается запись о закрытии. */
const CLOSED_ROWS: Partial<
  Record<RequestStatus, { label: string; Icon: typeof IconCheckCircle; cls: string }>
> = {
  CLOSED_WON: { label: 'Заявка закрыта: Счёт', Icon: IconCheckCircle, cls: 'text-green-600' },
  CLOSED_LOST: { label: 'Заявка закрыта: Отказ', Icon: IconXCircle, cls: 'text-red-600' },
  ARCHIVED: { label: 'Заявка в архиве', Icon: IconArchive, cls: 'text-gray-500' },
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU')
}

/** Тон даты контакта: просрочен — красный, сегодня — янтарный, будущий — серый. */
function dueTone(dueAt: string): 'red' | 'amber' | 'gray' {
  const due = new Date(dueAt)
  const today = new Date()
  due.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)
  if (due < today) return 'red'
  if (due.getTime() === today.getTime()) return 'amber'
  return 'gray'
}

const HISTORY_STATUS_LABELS: Record<string, string> = {
  DONE: 'Выполнено',
  CANCELLED: 'Отменено',
}

/**
 * Блок «Контроль КП» в карточке заявки (16.07.2026): таймлайн «КП создано →
 * КП отправлено» из существующих данных + внутреннее напоминание менеджера
 * «следующий контакт с клиентом» (одно активное на заявку, история касаний
 * в аккордеоне). Показывается только когда есть хотя бы одно КП или напоминание;
 * закупщику/РОЗ блок не рендерится (гейт в RequestDetailPage).
 */
export function KPControlPanel({ requestId, status, quoteSentAt, canEdit }: Props) {
  const queryClient = useQueryClient()
  const [modal, setModal] = useState<'create' | 'edit' | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)

  const closedRow = CLOSED_ROWS[status] ?? null

  const { data: followUps = [] } = useQuery({
    queryKey: ['follow-ups', requestId],
    queryFn: () => getFollowUps(requestId),
  })
  // КП-список уже закэширован KPAccordion/ActionsPanel — повторный запрос не летит.
  const { data: quotations = [] } = useQuery({
    queryKey: ['quotations', requestId],
    queryFn: () => getQuotations(requestId),
  })
  // Дата закрытия — из истории статусов (кэш общий с HistoryPanel внизу
  // страницы, ключ и staleTime совпадают). Грузим только для закрытых заявок.
  const { data: activity = [] } = useQuery({
    queryKey: ['requests', requestId, 'activity'],
    queryFn: () => getRequestActivity(requestId),
    staleTime: 30_000,
    enabled: closedRow !== null,
  })
  const closedAt = closedRow
    ? (activity.find((e) => e._type === 'status' && e.toStatus === status)?.createdAt ?? null)
    : null

  const open = followUps.find((f) => f.status === 'OPEN') ?? null
  const history = followUps.filter((f) => f.status !== 'OPEN')

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['follow-ups', requestId] })
    void queryClient.invalidateQueries({ queryKey: ['follow-up-alerts'] })
    void queryClient.invalidateQueries({ queryKey: ['requests'] })
  }

  const createMut = useMutation({
    mutationFn: (p: { dueAt: string; note: string }) =>
      createFollowUp(requestId, { dueAt: p.dueAt, note: p.note || null }),
    onSuccess: () => {
      invalidate()
      toast.success('Напоминание о контакте создано')
    },
  })
  const patchMut = useMutation({
    mutationFn: (p: {
      id: string
      dueAt?: string
      note?: string | null
      status?: 'DONE' | 'CANCELLED'
    }) => patchFollowUp(p.id, { dueAt: p.dueAt, note: p.note, status: p.status }),
    onSuccess: invalidate,
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Не удалось обновить напоминание'
      toast.error(msg)
    },
  })

  // Блок появляется после первого КП (по сценарию заказчика) либо если
  // напоминания уже есть (например, КП удалили, а история осталась).
  if (quotations.length === 0 && followUps.length === 0) return null

  const lastKpCreatedAt = quotations[0]?.createdAt ?? null
  const tone = open ? dueTone(open.dueAt) : 'gray'
  const toneCls =
    tone === 'red'
      ? 'bg-red-100 text-red-700'
      : tone === 'amber'
        ? 'bg-amber-100 text-amber-700'
        : 'bg-gray-100 text-gray-600'

  const markDone = async () => {
    if (!open) return
    patchMut.mutate(
      { id: open.id, status: 'DONE' },
      {
        onSuccess: () => {
          toast.success('Контакт отмечен выполненным')
          // Сразу предлагаем назначить следующий — цикл касаний клиента.
          setModal('create')
        },
      },
    )
  }

  const cancelOpen = async () => {
    if (!open) return
    const ok = await confirm({
      title: 'Отменить напоминание?',
      message: 'Напоминание о контакте уйдёт в историю со статусом «Отменено».',
      confirmText: 'Отменить напоминание',
    })
    if (!ok) return
    patchMut.mutate({ id: open.id, status: 'CANCELLED' })
  }

  const renderHistoryRow = (f: RequestFollowUp) => (
    <div key={f.id} className="flex items-start gap-2 text-xs text-gray-500">
      <span className="shrink-0">{fmtDate(f.dueAt)}</span>
      <span
        className={`shrink-0 inline-flex px-1.5 py-0.5 rounded text-[11px] font-medium ${
          f.status === 'DONE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}
      >
        {HISTORY_STATUS_LABELS[f.status] ?? f.status}
      </span>
      {f.note && <span className="text-gray-500 break-words">{f.note}</span>}
    </div>
  )

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Контроль КП</h3>
        {canEdit && !open && !closedRow && (
          <button
            onClick={() => setModal('create')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors"
          >
            <IconBell className="w-4 h-4" />
            Назначить контакт
          </button>
        )}
      </div>

      <div className="space-y-1.5 text-sm">
        <div className="flex items-center gap-2 text-gray-700">
          <IconDocument className="w-4 h-4 shrink-0 text-gray-400" />
          <span>КП создано</span>
          <span className="font-medium">
            {lastKpCreatedAt ? fmtDate(lastKpCreatedAt) : '—'}
            {quotations.length > 1 && (
              <span className="ml-1 text-xs text-gray-400 font-normal">
                (всего {quotations.length})
              </span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <IconSend className="w-4 h-4 shrink-0 text-gray-400" />
          <span>КП отправлено</span>
          {quoteSentAt ? (
            <span className="font-medium">{fmtDate(quoteSentAt)}</span>
          ) : (
            <span className="text-xs text-amber-600">не отмечено</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-gray-700">
          <IconBell className="w-4 h-4 shrink-0 text-gray-400" />
          <span>Следующий контакт</span>
          {open ? (
            <>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${toneCls}`}
              >
                {fmtDate(open.dueAt)}
                {tone === 'red' && ' — просрочен'}
                {tone === 'amber' && ' — сегодня'}
              </span>
              {canEdit && (
                <span className="flex items-center gap-1">
                  <button
                    onClick={() => void markDone()}
                    disabled={patchMut.isPending}
                    className="px-2 py-0.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    ✓ Выполнено
                  </button>
                  <button
                    onClick={() => setModal('edit')}
                    disabled={patchMut.isPending}
                    className="px-2 py-0.5 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    Изменить
                  </button>
                  <button
                    onClick={() => void cancelOpen()}
                    disabled={patchMut.isPending}
                    className="px-2 py-0.5 text-xs border border-gray-300 text-gray-400 rounded hover:bg-gray-50 hover:text-gray-600 disabled:opacity-50"
                    title="Отменить напоминание"
                  >
                    ×
                  </button>
                </span>
              )}
            </>
          ) : (
            <span className="text-xs text-gray-400">не назначен</span>
          )}
        </div>
        {open?.note && <p className="pl-6 text-xs text-gray-500 break-words">«{open.note}»</p>}
        {closedRow && (
          <div className={`flex items-center gap-2 ${closedRow.cls}`}>
            <closedRow.Icon className="w-4 h-4 shrink-0" />
            <span className="font-medium">{closedRow.label}</span>
            {closedAt && <span>{fmtDate(closedAt)}</span>}
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div className="pt-1 border-t border-gray-100">
          <button
            onClick={() => setHistoryOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
          >
            <IconChevron
              className={`w-3 h-3 transition-transform ${historyOpen ? 'rotate-90' : ''}`}
            />
            История касаний ({history.length})
          </button>
          {historyOpen && <div className="mt-1.5 space-y-1">{history.map(renderHistoryRow)}</div>}
        </div>
      )}

      {modal === 'create' && (
        <FollowUpModal
          cancelLabel="Пропустить"
          onConfirm={async (dueAt, note) => {
            await createMut.mutateAsync({ dueAt, note })
            setModal(null)
          }}
          onCancel={() => setModal(null)}
        />
      )}
      {modal === 'edit' && open && (
        <FollowUpModal
          title="Изменить контакт"
          initialDueAt={open.dueAt}
          initialNote={open.note}
          onConfirm={async (dueAt, note) => {
            await patchMut.mutateAsync({ id: open.id, dueAt, note: note || null })
            setModal(null)
          }}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  )
}
